import axios from "../../../api/axios";

/**
 * API endpoints for color coding operations
 */
const ENDPOINTS = {
  COLOR_CONFIG: "v2/master_sheet/color-config",
  COLOR_CONFIG_BY_ID: (id) => `v2/master_sheet/color-config/${id}`,
  COLOR_CONFIG_BY_PROPERTY: (propertyId) => `v2/master_sheet/color-config/property/${propertyId}`,
  COLOR_RULE: (id) => `v2/master_sheet/color-config/${id}/rule`,
  SPECIFIC_COLOR_RULE: (configId, ruleId) => `v2/master_sheet/color-config/${configId}/rule/${ruleId}`,
  COLOR_CONFIG_STATUS: (id) => `v2/master_sheet/color-config/${id}/status`,
};

/**
 * Fetches all color configurations
 * @returns {Promise<Object>} All color configurations
 */
export const getAllColorConfigs = async () => {
  const { data } = await axios.get(ENDPOINTS.COLOR_CONFIG);
  return data;
};

/**
 * Fetches color configuration by ID
 * @param {string} id - Color configuration ID
 * @returns {Promise<Object>} Color configuration data
 */
export const getColorConfig = async (id) => {
  const { data } = await axios.get(ENDPOINTS.COLOR_CONFIG_BY_ID(id));
  return data;
};

/**
 * Fetches color configurations by property ID
 * @param {string} propertyId - Property ID
 * @param {Object} params - Query parameters (optional)
 * @returns {Promise<Object>} Filtered color configuration data
 */
export const getColorConfigsByProperty = async (propertyId, params = {}) => {
  const { data } = await axios.get(ENDPOINTS.COLOR_CONFIG_BY_PROPERTY(propertyId), { params });
  return data;
};

/**
 * Creates a new color configuration
 * @param {Object} configData - Color configuration data
 * @returns {Promise<Object>} Created color configuration
 */
export const createColorConfig = async (configData) => {
  const { data } = await axios.post(ENDPOINTS.COLOR_CONFIG, configData);
  return data;
};

/**
 * Updates an existing color configuration
 * @param {string} id - Color configuration ID
 * @param {Object} configData - Updated color configuration data
 * @returns {Promise<Object>} Updated color configuration
 */
export const updateColorConfig = async (id, configData) => {
  const { data } = await axios.put(ENDPOINTS.COLOR_CONFIG_BY_ID(id), configData);
  return data;
};

/**
 * Deletes a color configuration
 * @param {string} id - Color configuration ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteColorConfig = async (id) => {
  const { data } = await axios.delete(ENDPOINTS.COLOR_CONFIG_BY_ID(id));
  return data;
};

/**
 * Adds a new color rule to a configuration
 * @param {string} id - Color configuration ID
 * @param {Object} ruleData - Color rule data
 * @returns {Promise<Object>} Created color rule
 */
export const addColorRule = async (id, ruleData) => {
  const { data } = await axios.put(ENDPOINTS.COLOR_RULE(id), ruleData);
  return data;
};

/**
 * Updates an existing color rule
 * @param {string} configId - Color configuration ID
 * @param {string} ruleId - Color rule ID
 * @param {Object} ruleData - Updated color rule data
 * @returns {Promise<Object>} Updated color rule
 */
export const updateColorRule = async (configId, ruleId, ruleData) => {
  const { data } = await axios.put(ENDPOINTS.SPECIFIC_COLOR_RULE(configId, ruleId), ruleData);
  return data;
};

/**
 * Deletes a color rule
 * @param {string} configId - Color configuration ID
 * @param {string} ruleId - Color rule ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteColorRule = async (configId, ruleId) => {
  const { data } = await axios.delete(ENDPOINTS.SPECIFIC_COLOR_RULE(configId, ruleId));
  return data;
};

/**
 * Toggles color configuration status
 * @param {string} id - Color configuration ID
 * @param {Object} statusData - Status data
 * @returns {Promise<Object>} Updated status
 */
export const toggleColorConfigStatus = async (id, statusData) => {
  const { data } = await axios.patch(ENDPOINTS.COLOR_CONFIG_STATUS(id), statusData);
  return data;
};